/**
 * Layer-to-layer problem pool carry-forward.
 *
 * Extracted verbatim from `VIFRulesAddSub.ts` so the CreateJS games and the
 * DOM boards draw from identical logic. Behaviour is unchanged: both helpers
 * mutate `topEQs`/`bottomAnswers` in place, exactly as the legacy code does.
 */

/** How many previously-chosen entries `pickRandomThenCarryUnchosen` keeps. */
const CARRY_FORWARD_PICK_COUNT = 6;

/**
 * Collects the values currently in topEQs/bottomAnswers into arrHolder /
 * arrHolder2, clears both arrays, then re-pushes entries in index order per
 * `onSnapshot`'s decision.
 */
function snapshotThenRefill(
  topEQs: string[],
  bottomAnswers: string[],
  onSnapshot: (arrHolder: string[], arrHolder2: string[]) => void,
): void {
  const arrHolder: string[] = [];
  const arrHolder2: string[] = [];
  for (let j = 0; j < topEQs.length; j++) {
    arrHolder.push(topEQs[j]!);
    arrHolder2.push(bottomAnswers[j]!);
  }
  topEQs.length = 0;
  bottomAnswers.length = 0;
  onSnapshot(arrHolder, arrHolder2);
}

/** Appends the entries not yet chosen (per blnChosenRow) in index order. */
function appendUnchosen(
  topEQs: string[],
  bottomAnswers: string[],
  arrHolder: string[],
  arrHolder2: string[],
  blnChosenRow: number[],
): void {
  for (let j = 0; j < blnChosenRow.length; j++) {
    if (blnChosenRow[j] == 0) {
      topEQs.push(arrHolder[j]!);
      bottomAnswers.push(arrHolder2[j]!);
    }
  }
}

/**
 * Collects the values currently in topEQs/bottomAnswers, then refills them
 * with only the entries not yet chosen (per blnChosenRow) in a prior layer.
 */
export function carryForwardUnchosen(
  topEQs: string[],
  bottomAnswers: string[],
  blnChosenRow: number[],
): void {
  snapshotThenRefill(topEQs, bottomAnswers, (arrHolder, arrHolder2) => {
    appendUnchosen(topEQs, bottomAnswers, arrHolder, arrHolder2, blnChosenRow);
  });
}

/**
 * Same as `carryForwardUnchosen`, but first randomly picks six of the
 * previously chosen (blnChosenRow == 1) entries before carrying forward the
 * unchosen ones.
 *
 * `rng` defaults to `Math.random`, matching the legacy call site; it is
 * injectable so the DOM board can be tested deterministically.
 */
export function pickSixRandomThenCarryUnchosen(
  topEQs: string[],
  bottomAnswers: string[],
  blnChosenRow: number[],
  arr: number[],
  rng: () => number = Math.random,
): void {
  snapshotThenRefill(topEQs, bottomAnswers, (arrHolder, arrHolder2) => {
    let counter = 0;
    while (counter < CARRY_FORWARD_PICK_COUNT) {
      let j = Math.floor(rng() * arrHolder.length);
      while (arr[j] == 1) {
        j = Math.floor(rng() * arrHolder.length);
      }
      if (blnChosenRow[j] == 1) {
        topEQs.push(arrHolder[j]!);
        bottomAnswers.push(arrHolder2[j]!);
        counter++;
        arr[j] = 1;
      }
    }

    // Then the entries that were not chosen in the previous layer.
    appendUnchosen(topEQs, bottomAnswers, arrHolder, arrHolder2, blnChosenRow);
  });
}
